import React, { useState, useEffect } from 'react';
import { 
  Heart, Briefcase, Coins, Users, BookOpen, Plane, Sparkles, 
  ChevronDown, ChevronRight, Loader2, User, Calendar, MapPin,
  Activity, Brain, Shield, TrendingUp, AlertCircle, Star
} from 'lucide-react';
import API_URL from '../config/apiConfig';

// The 4 users data
const USERS = [
  {
    name: "Mandeep",
    year: 1997, month: 5, day: 14,
    hour: 12, minute: 17,
    latitude: 30.3165, longitude: 78.0322,
    place: "Dehradun, Uttarakhand",
    gender: "male"
  },
  {
    name: "Parveen",
    year: 1990, month: 5, day: 16,
    hour: 16, minute: 15,
    latitude: 32.7266, longitude: 74.8570,
    place: "Jammu",
    gender: "female"
  },
  {
    name: "Sofi Zubair",
    year: 1988, month: 6, day: 27,
    hour: 12, minute: 30,
    latitude: 34.4003, longitude: 74.2663,
    place: "Handwara, Kashmir",
    gender: "male"
  },
  {
    name: "Tejpal Singh",
    year: 1994, month: 5, day: 5,
    hour: 11, minute: 0,
    latitude: 32.7266, longitude: 74.8570,
    place: "Jammu, Kashmir",
    gender: "male"
  }
];

const DOMAINS = [
  { key: 'health', name: 'Health', icon: Heart, color: 'emerald', endpoint: '/api/health-timeline/complete' },
  { key: 'career', name: 'Career', icon: Briefcase, color: 'blue', endpoint: '/api/career-timeline/complete' },
  { key: 'wealth', name: 'Wealth', icon: Coins, color: 'amber', endpoint: '/api/wealth-timeline/complete' },
  { key: 'marriage', name: 'Marriage', icon: Users, color: 'pink', endpoint: '/api/marriage-timeline/complete' },
  { key: 'family', name: 'Family', icon: Users, color: 'purple', endpoint: '/api/family-timeline/complete' },
  { key: 'education', name: 'Education', icon: BookOpen, color: 'cyan', endpoint: '/api/education-timeline/complete' },
  { key: 'travel', name: 'Travel', icon: Plane, color: 'orange', endpoint: '/api/travel-timeline/complete' },
  { key: 'spiritual', name: 'Spiritual', icon: Sparkles, color: 'violet', endpoint: '/api/spiritual-timeline/complete' },
];

const Section = ({ title, icon: Icon, color, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const colorClasses = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    pink: 'bg-pink-500/10 border-pink-500/30 text-pink-400',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
    cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
    orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
    violet: 'bg-violet-500/10 border-violet-500/30 text-violet-400',
  };

  return (
    <div className={`border rounded-lg mb-3 ${colorClasses[color] || colorClasses.emerald}`}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5" />}
          <span className="font-semibold">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {isOpen && (
        <div className="px-3 pb-3 border-t border-white/10">
          {children}
        </div>
      )}
    </div>
  );
};

const DataDisplay = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) return <span className="text-gray-500">N/A</span>;
  
  if (typeof data === 'boolean') return <span className={data ? 'text-emerald-400' : 'text-rose-400'}>{data ? 'Yes' : 'No'}</span>;
  
  if (typeof data === 'number') return <span className="text-cyan-400">{data}</span>;
  
  if (typeof data === 'string') return <span className="text-gray-300">{data}</span>;
  
  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-gray-500">[]</span>;
    return (
      <div className="ml-2 mt-1">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-start gap-1 mb-1">
            <span className="text-gray-500">•</span>
            <DataDisplay data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  
  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="text-gray-500">{'{}'}</span>;
    return (
      <div className={`${depth > 0 ? 'ml-2 mt-1 pl-2 border-l border-white/10' : ''}`}>
        {entries.map(([key, value]) => (
          <div key={key} className="mb-1">
            <span className="text-amber-400 font-medium">{key.replace(/_/g, ' ')}: </span>
            <DataDisplay data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  
  return <span className="text-gray-400">{String(data)}</span>;
};

const UserDomainData = ({ user, domainData, domainKey }) => {
  const [expanded, setExpanded] = useState({});
  
  const toggleSection = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!domainData) {
    return <div className="text-gray-500 text-center py-4">No data available</div>;
  }

  // Extract nested data if wrapped
  let data = domainData;
  if (data.success !== undefined && data.data) {
    data = data.data;
  }

  // Render different sections based on domain
  return (
    <div className="space-y-2 mt-2">
      {Object.entries(data).map(([key, value]) => {
        const isExpandable = typeof value === 'object' && value !== null;
        const isExpanded = expanded[key];
        
        return (
          <div key={key} className="bg-white/5 rounded-lg overflow-hidden">
            <button 
              onClick={() => isExpandable && toggleSection(key)}
              className="w-full flex items-center justify-between p-2 text-left hover:bg-white/5"
            >
              <span className="text-sm font-medium text-amber-300 capitalize">
                {key.replace(/_/g, ' ')}
              </span>
              {isExpandable && (
                isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {(isExpanded || !isExpandable) && (
              <div className="px-2 pb-2 text-sm">
                <DataDisplay data={value} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CompletePredictionsPage = () => {
  const [selectedUser, setSelectedUser] = useState(0);
  const [loading, setLoading] = useState({});
  const [allData, setAllData] = useState({});
  const [expandedDomains, setExpandedDomains] = useState({});

  // Fetch data for a specific user and domain
  const fetchDomainData = async (userIndex, domainKey) => {
    const user = USERS[userIndex];
    const domain = DOMAINS.find(d => d.key === domainKey);
    if (!domain) return;

    const loadingKey = `${userIndex}-${domainKey}`;
    setLoading(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await fetch(`${API_URL}${domain.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: user.name,
          year: user.year,
          month: user.month,
          day: user.day,
          hour: user.hour,
          minute: user.minute,
          latitude: user.latitude,
          longitude: user.longitude,
          gender: user.gender,
          place: user.place
        })
      });

      const result = await response.json();
      setAllData(prev => ({
        ...prev,
        [`${userIndex}-${domainKey}`]: result
      }));
    } catch (error) {
      console.error(`Error fetching ${domainKey} for ${user.name}:`, error);
      setAllData(prev => ({
        ...prev,
        [`${userIndex}-${domainKey}`]: { error: error.message }
      }));
    }

    setLoading(prev => ({ ...prev, [loadingKey]: false }));
  };

  // Fetch all domains for selected user
  const fetchAllDomainsForUser = async (userIndex) => {
    for (const domain of DOMAINS) {
      await fetchDomainData(userIndex, domain.key);
    }
  };

  // Fetch all data for all users
  const fetchAllData = async () => {
    for (let i = 0; i < USERS.length; i++) {
      await fetchAllDomainsForUser(i);
    }
  };

  const toggleDomain = (userIndex, domainKey) => {
    const key = `${userIndex}-${domainKey}`;
    const dataKey = `${userIndex}-${domainKey}`;
    
    // Fetch if not already loaded
    if (!allData[dataKey] && !loading[dataKey]) {
      fetchDomainData(userIndex, domainKey);
    }
    
    setExpandedDomains(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white p-4">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
            Complete 8-Domain Predictions
          </h1>
          <p className="text-gray-400 mt-2">All Backend Data for 4 Users</p>
        </div>

        {/* Load All Button */}
        <div className="flex justify-center mb-6 gap-4">
          <button
            onClick={fetchAllData}
            className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg font-medium hover:opacity-90 transition"
          >
            Load All Data
          </button>
        </div>

        {/* User Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {USERS.map((user, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedUser(idx)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedUser === idx 
                  ? 'bg-amber-500 text-black' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {user.name}
              </div>
            </button>
          ))}
        </div>

        {/* All Users View */}
        <div className="space-y-8">
          {USERS.map((user, userIndex) => (
            <div 
              key={userIndex} 
              className={`bg-white/5 rounded-xl p-4 border ${
                selectedUser === userIndex ? 'border-amber-500/50' : 'border-white/10'
              }`}
            >
              {/* User Header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-amber-400">{user.name}</h2>
                    <div className="flex items-center gap-3 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {`${user.day.toString().padStart(2, '0')}-${user.month.toString().padStart(2, '0')}-${user.year}`}
                        {` ${user.hour.toString().padStart(2, '0')}:${user.minute.toString().padStart(2, '0')}`}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {user.place}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => fetchAllDomainsForUser(userIndex)}
                  className="px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 transition"
                >
                  Load All Domains
                </button>
              </div>

              {/* Domains Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {DOMAINS.map((domain) => {
                  const dataKey = `${userIndex}-${domain.key}`;
                  const isLoading = loading[dataKey];
                  const data = allData[dataKey];
                  const isExpanded = expandedDomains[dataKey];
                  const DomainIcon = domain.icon;

                  const colorClasses = {
                    emerald: 'border-emerald-500/30 hover:border-emerald-500/50',
                    blue: 'border-blue-500/30 hover:border-blue-500/50',
                    amber: 'border-amber-500/30 hover:border-amber-500/50',
                    pink: 'border-pink-500/30 hover:border-pink-500/50',
                    purple: 'border-purple-500/30 hover:border-purple-500/50',
                    cyan: 'border-cyan-500/30 hover:border-cyan-500/50',
                    orange: 'border-orange-500/30 hover:border-orange-500/50',
                    violet: 'border-violet-500/30 hover:border-violet-500/50',
                  };

                  const textColors = {
                    emerald: 'text-emerald-400',
                    blue: 'text-blue-400',
                    amber: 'text-amber-400',
                    pink: 'text-pink-400',
                    purple: 'text-purple-400',
                    cyan: 'text-cyan-400',
                    orange: 'text-orange-400',
                    violet: 'text-violet-400',
                  };

                  return (
                    <div 
                      key={domain.key}
                      className={`bg-white/5 rounded-lg border transition ${colorClasses[domain.color]}`}
                    >
                      <button
                        onClick={() => toggleDomain(userIndex, domain.key)}
                        className="w-full flex items-center justify-between p-3"
                      >
                        <div className="flex items-center gap-2">
                          <DomainIcon className={`w-5 h-5 ${textColors[domain.color]}`} />
                          <span className={`font-semibold ${textColors[domain.color]}`}>
                            {domain.name}
                          </span>
                          {data && !data.error && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                              Loaded
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                          {isExpanded ? 
                            <ChevronDown className="w-4 h-4 text-gray-400" /> : 
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          }
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-white/10 max-h-96 overflow-y-auto">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                            </div>
                          ) : data?.error ? (
                            <div className="text-rose-400 py-4 text-center">
                              Error: {data.error}
                            </div>
                          ) : data ? (
                            <UserDomainData 
                              user={user} 
                              domainData={data} 
                              domainKey={domain.key} 
                            />
                          ) : (
                            <div className="text-gray-500 py-4 text-center">
                              Click to load data
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompletePredictionsPage;
